import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Loading from '../Common/Loading';
import CustomAlert from '../Common/CustomAlert';
import submit_n from '../Common/Images/submit_n.gif';
import submit_o from '../Common/Images/submit_o.gif';
import cancel_n from '../Common/Images/cancel_n.gif';
import cancel_o from '../Common/Images/cancel_o.gif';

const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;
const ITEMS_PER_PAGE = 50;

interface CourtesyCallRecord {
    SchoolCode: string;
    FamilyCode: string;
    Login: string;
    Major: string;
    Minor: string;
    LName: string;
    Address1: string;
    Suburb: string;
    Phone: string;
    State: string;
    Age: number | string;
    ActionDate: string;
    ToLogin: string;
    DateOfSale: string;
    ActivityStatus: string;
}

const styles = {
    container: { 
        position: 'absolute' as 'absolute',
        top: 0, left: 0, right: 0, bottom: 0,
        display: 'flex', flexDirection: 'column' as 'column',
        fontFamily: 'Verdana, Arial, Helvetica, sans-serif', fontSize: '11px',
        backgroundColor: '#FFFFFF', overflow: 'hidden'
    },

    headerSection: {
        backgroundColor: 'aliceblue',
        borderBottom: '1px solid #999',
        flexShrink: 0,
        padding: '10px',
        textAlign: 'center' as 'center'
    },

    filterTable: {
        width: '100%',
        backgroundColor: 'aliceblue',
        borderCollapse: 'collapse' as 'collapse',
        border: '1px solid gray',
    },

    tableContainer: {
        flex: 1,
        overflow: 'auto',
        backgroundColor: '#FFFFFF'
    },

  dataTable: {
    borderCollapse: 'collapse' as const,
    fontSize: '11px',
    border: '1px solid #80a0ff',
    display: 'inline-table',
    whiteSpace: 'nowrap' as const
},
    th: {
        fontFamily: 'Arial',
        backgroundColor: '#C7DDFD',
        color: '#2859ac',
        fontSize: '10pt',
        padding: '8px',
        fontWeight: 'normal',
        border: '1px solid #80a0ff',
        textAlign: 'left' as 'left',
        position: 'sticky' as 'sticky',
        top: 0,
        zIndex: 1
    },

    td: {
        padding: '8px',
        fontFamily: 'Arial',
        fontSize: '10pt',
        borderRight: '1px solid #80a0ff', 
    },

    paginationBar: {
        fontFamily: 'Arial',
        padding: '8px',
        textAlign: 'center' as 'center',
        backgroundColor: '#C7DDFD',
        borderTop: '1px solid #80a0ff',
        fontSize: '12pt',
        color: 'black',
        flexShrink: 0
    },

    pageLink: {
        cursor: 'pointer',
        margin: '0 5px',
        textDecoration: 'underline',
        color: '#0000FF'
    },

    pageLinkDisabled: {
        margin: '0 5px',
        color: '#999',
        textDecoration: 'none',
        cursor: 'default'
    }
};

const EL_CourtesyCallList: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const sParam = searchParams.get('S') || 'A';
    const fParam = searchParams.get('F') || 'AA';
    const lParam = searchParams.get('L') || 'System';

    const [countries, setCountries] = useState<string[]>([]);
    const [selectedCountry, setSelectedCountry] = useState<string>('');
    const [stateFilter, setStateFilter] = useState<string>('');
    const [distributors, setDistributors] = useState<{value: string, label: string}[]>([]);
    const [selectedDistributor, setSelectedDistributor] = useState<string>('');
    const [callType, setCallType] = useState<string>('');
    
    const defaultFromDate = new Date();
    defaultFromDate.setMonth(defaultFromDate.getMonth() - 1);
    const [fromDate, setFromDate] = useState<string>(defaultFromDate.toISOString().split('T')[0]);
    const [toDate, setToDate] = useState<string>(new Date().toISOString().split('T')[0]);

    const [tableData, setTableData] = useState<CourtesyCallRecord[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [activeRowId, setActiveRowId] = useState<string | null>(null);

    const [isGoHovered, setIsGoHovered] = useState(false);
    const [isCancelHovered, setIsCancelHovered] = useState(false);

    const [alertState, setAlertState] = useState({
        show: false,
        message: '',
        isSuccess: false
    });

    const showAlert = (msg: string, isSuccess: boolean = false) => {
        setAlertState({ show: true, message: msg, isSuccess });
    };

    const handleCloseAlert = () => {
        setAlertState(prev => ({ ...prev, show: false }));
    };

    useEffect(() => {
        const fetchCountries = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}api/get_countries/`);
                if (response.ok) {
                    const data = await response.json();
                    if (data.countries && Array.isArray(data.countries)) {
                        setCountries(data.countries);
                    } else if (Array.isArray(data)) {
                        const apiCountries = data.map((item: any) => item.major || item.Country || item);
                        setCountries(apiCountries);
                    }
                }
            } catch (err) {
                console.error('Failed to fetch countries:', err);
            }
        };
        fetchCountries();
    }, []);

    useEffect(() => {
        const fetchDistributors = async () => {
            if (!selectedCountry || selectedCountry === '-Select-') {
                setDistributors([]);
                return;
            }
            try {
                const response = await fetch(`${API_BASE_URL}api/get_courtesy_call_distributors/?country=${encodeURIComponent(selectedCountry)}`);
                if (response.ok) {
                    const data = await response.json();
                    setDistributors(data.distributors || []);
                }
            } catch (err) {
                console.error('Failed to fetch distributors:', err);
            }
        };
        fetchDistributors();
    }, [selectedCountry]);

    const handleGoClick = async () => {
        if (!selectedCountry || selectedCountry === '-Select-') {
            showAlert("Please Select the Country.", false);
            return;
        }

        setLoading(true);
        setCurrentPage(1);
        setActiveRowId(null);
        try {
            const params = new URLSearchParams({
                country: selectedCountry,
                distributor: selectedDistributor,
                state: stateFilter,
                callType: callType,
                fromDate: fromDate,
                toDate: toDate
            });

            const response = await fetch(`${API_BASE_URL}api/get_courtesy_call_list/?${params.toString()}`);
            
            if (!response.ok) throw new Error(`HTTP Error: ${response.status}`);
            
            const result = await response.json();
            if (result.error) throw new Error(result.error);

            setTableData(result.data || []);
        } catch (error: any) {
            console.error("Fetch Error:", error);
            showAlert(error.message || 'Error loading call list data', false);
        } finally {
            setLoading(false);
        }
    };

    const handleRowClick = (record: CourtesyCallRecord) => {
        setActiveRowId(`${record.SchoolCode}-${record.FamilyCode}-${record.Login}-${record.Major}`);

        const url = `/SUPPORT/EL_AddEditTask?SchoolCode=${encodeURIComponent(record.SchoolCode)}&FamilyCode=${encodeURIComponent(record.FamilyCode)}&Login=${encodeURIComponent(record.Login)}&FilterType=CustEntry&S=${encodeURIComponent(sParam)}&F=${encodeURIComponent(fParam)}&L=${encodeURIComponent(lParam)}&CourtesyCall=${encodeURIComponent(record.Major)}&Minor=${encodeURIComponent(record.Minor)}&FromCourtesyCall=Y`;
        window.open(url, '_blank');
    };

    const indexOfLastItem = currentPage * ITEMS_PER_PAGE;
    const indexOfFirstItem = indexOfLastItem - ITEMS_PER_PAGE;
    const currentItems = tableData.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(tableData.length / ITEMS_PER_PAGE) || 1;

    const changePage = (newPage: number) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    return (
        <div style={styles.container}>
            {loading && (
                <div style={{position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(255,255,255,0.7)', zIndex: 100, display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                    <Loading />
                </div>
            )}
            
            <div style={styles.headerSection}>
                <table cellPadding="5" cellSpacing="1" border={1} style={styles.filterTable}>
                    <tbody>
                        <tr>
                            <td colSpan={6} style={{ textAlign: 'center', fontFamily: 'Trebuchet MS', fontSize: 'large', padding: '10px' }}>
                                <b>COURTESY CALL LIST</b>
                            </td>
                        </tr>
                        <tr style={{ fontWeight: 'bold' }}>
                            <td style={{ width: '12%', fontSize: '10pt', fontFamily: 'Arial', textAlign:"left" }}>Country</td>
                            <td style={{ width: '12%', fontSize: '10pt', fontFamily: 'Arial', textAlign:"left" }}>State</td>
                            <td style={{ width: '14%', fontSize: '10pt', fontFamily: 'Arial', textAlign:"left" }}>Distributor</td>
                            <td style={{ width: '11%', fontSize: '10pt', fontFamily: 'Arial', textAlign:"left" }}>Call Type</td>
                            <td style={{ width: '30%', fontSize: '10pt', fontFamily: 'Arial', textAlign:"left" }}>Courtesy Call Scheduled Date</td>
                            <td rowSpan={2} style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center', gap: '10px' }}>
                                    <img 
                                        src={isGoHovered ? submit_o : submit_n} 
                                        alt="Go" 
                                        onClick={handleGoClick}
                                        onMouseEnter={() => setIsGoHovered(true)}
                                        onMouseLeave={() => setIsGoHovered(false)}
                                        style={{ cursor: 'pointer' }}
                                    />
                                    <img 
                                        src={isCancelHovered ? cancel_o : cancel_n} 
                                        alt="Cancel" 
                                        onClick={() => navigate(-1)}
                                        onMouseEnter={() => setIsCancelHovered(true)}
                                        onMouseLeave={() => setIsCancelHovered(false)}
                                        style={{ cursor: 'pointer' }}
                                    />
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <select className="cssDropDown" value={selectedCountry} onChange={(e) => setSelectedCountry(e.target.value)} style={{ width: '100%', fontSize: '10pt', fontFamily: 'Verdana' }}>
                                    <option value="">-Select-</option>
                                    {countries.map((c, idx) => <option key={idx} value={c}>{c}</option>)}
                                </select>
                            </td>
                            <td>
                                <input type="text" className="cssTextBox" value={stateFilter} onChange={(e) => setStateFilter(e.target.value)} style={{ width: '90%', fontSize: '10pt', fontFamily: 'Verdana' }} maxLength={30} />
                            </td>
                            <td>
                                <select className="cssDropDown" value={selectedDistributor} onChange={(e) => setSelectedDistributor(e.target.value)} style={{ width: '100%', fontSize: '10pt', fontFamily: 'Verdana' }}>
                                    <option value="">-Select-</option>
                                    {distributors.map((d, idx) => <option key={idx} value={d.value}>{d.label}</option>)}
                                </select>
                            </td>
                            <td>
                                <select className="cssDropDown" value={callType} onChange={(e) => setCallType(e.target.value)} style={{ width: '100%', fontSize: '10pt', fontFamily: 'Verdana' }}>
                                    <option value="">-Select-</option>
                                    <option value="1">Courtesy Call 1</option>
                                    <option value="2">Courtesy Call 2</option>
                                    <option value="3">Courtesy Call 3</option>
                                </select>
                            </td>
                            <td style={{ fontSize: '10pt', fontFamily: 'Verdana' }}>
                                From: &nbsp;
                                <input type="date" className="cssTextBox" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                                &nbsp;&nbsp;&nbsp;&nbsp;
                                To: &nbsp;
                                <input type="date" className="cssTextBox" value={toDate} onChange={(e) => setToDate(e.target.value)} />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div style={styles.tableContainer}>
                <table style={styles.dataTable}>
                    <thead>
                        <tr>
                            <th style={{ ...styles.th, width: '3%', textAlign: 'center' }}>#</th>
                            <th style={styles.th}>Login</th>
                            <th style={styles.th}>Type</th>
                            <th style={styles.th}>Surname</th>
                            <th style={styles.th}>Address</th>
                            <th style={styles.th}>Suburb</th>
                            <th style={styles.th}>Phone</th>
                            <th style={styles.th}>Age from<br/>Install</th>
                            <th style={styles.th}>Courtesy Call<br/>Scheduled Date</th>
                            <th style={{...styles.th, width:"100%"}}>Assigned To</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentItems.length === 0 ? (
                            <tr><td colSpan={10} style={{ textAlign: 'center', padding: '20px', color: '#666' }}></td></tr>
                        ) : (
                            currentItems.map((row, idx) => {
                                const currentRowId = `${row.SchoolCode}-${row.FamilyCode}-${row.Login}-${row.Major}`;
                                const isActive = activeRowId === currentRowId;

                                return (
                                    <tr 
                                        key={idx}
                                        onClick={() => handleRowClick(row)}
                                        style={{ 
                                            cursor: 'pointer',
                                            backgroundColor: isActive ? 'orange' : (idx % 2 === 0 ? '#F4FAFF' : 'white'),
                                            color: isActive ? 'white' : (row.ActivityStatus === 'C' ? 'red' : 'black'),
                                            // borderBottom: '1px solid #80a0ff'
                                        }}
                                    >
                                        <td style={{ ...styles.td, textAlign: 'center' }}>{indexOfFirstItem + idx + 1}</td>
                                        <td style={styles.td}>{row.Login}</td>
                                        <td style={styles.td}>{row.Major}</td>
                                        <td style={styles.td}>{row.LName}</td>
                                        <td style={styles.td}>{row.Address1}</td>
                                        <td style={styles.td}>{row.Suburb}</td>
                                        <td style={styles.td}>{row.Phone}</td>
                                        <td style={styles.td}>{row.Age}</td>
                                        <td style={styles.td}>{row.ActionDate ? new Date(row.ActionDate).toLocaleDateString('en-GB') : ''}</td>
                                        <td style={styles.td}>{row.ToLogin}</td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {tableData.length > 0 && (
                <div style={styles.paginationBar}>
                    <span
                        style={currentPage === 1 ? styles.pageLinkDisabled : styles.pageLink}
                        onClick={() => changePage(1)}
                    >
                        &lt;&lt;
                    </span>
                    <span
                        style={currentPage === 1 ? styles.pageLinkDisabled : styles.pageLink}
                        onClick={() => changePage(currentPage - 1)}
                    >
                        &lt;
                    </span>
                    
                    <span style={{color: 'black', margin: '0 5px'}}>
                        {currentPage} of {totalPages}
                    </span>

                    <span
                        style={currentPage === totalPages ? styles.pageLinkDisabled : styles.pageLink} 
                        onClick={() => changePage(currentPage + 1)}
                    >
                        &gt;
                    </span>
                    <span
                        style={currentPage === totalPages ? styles.pageLinkDisabled : styles.pageLink}
                        onClick={() => changePage(totalPages)}
                    >
                        &gt;&gt;
                    </span>
                </div>
            )}

            {alertState.show && (
                <CustomAlert 
                    message={alertState.message}
                    onClose={handleCloseAlert}
                />
            )}
        </div>
    );
};

export default EL_CourtesyCallList;